import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from 'react';

export interface MotionPreferences {
  reducedMotion: boolean;
  reducedTransparency: boolean;
  increasedContrast: boolean;
}

const DEFAULT_PREFERENCES: MotionPreferences = {
  reducedMotion: false,
  reducedTransparency: false,
  increasedContrast: false,
};

const MotionPreferencesContext = createContext<MotionPreferences>(DEFAULT_PREFERENCES);

function useMediaPreference(query: string): boolean {
  const [matches, setMatches] = useState(() =>
    typeof window === 'undefined' || typeof window.matchMedia !== 'function'
      ? false
      : window.matchMedia(query).matches,
  );

  useEffect(() => {
    if (typeof window.matchMedia !== 'function') return;
    const media = window.matchMedia(query);
    const update = () => setMatches(media.matches);
    update();
    media.addEventListener('change', update);
    return () => media.removeEventListener('change', update);
  }, [query]);

  return matches;
}

export function MotionPreferencesProvider({ children }: { children: ReactNode }) {
  const reducedMotion = useMediaPreference('(prefers-reduced-motion: reduce)');
  const reducedTransparency = useMediaPreference('(prefers-reduced-transparency: reduce)');
  const increasedContrast = useMediaPreference('(prefers-contrast: more)');

  const value = useMemo(
    () => ({ reducedMotion, reducedTransparency, increasedContrast }),
    [increasedContrast, reducedMotion, reducedTransparency],
  );

  return (
    <MotionPreferencesContext.Provider value={value}>
      {children}
    </MotionPreferencesContext.Provider>
  );
}

export function useMotionPreferences(): MotionPreferences {
  return useContext(MotionPreferencesContext);
}
